import React, { useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import CancelIcon from '@mui/icons-material/Cancel';
import { Box, Button, IconButton, InputAdornment, Stack, TextField, Tooltip } from '@mui/material';

import useToggleState from '~/hooks/useToggleState';

interface Props {
  maxRefundAmount: number;
  formRef: any;
  handleLateChangeOfMindFieldChange: any;
  onCustomerRefundAmountChange: (amount: string) => void;
  currencyCode: string;
}

export default function CustomAmount({
  maxRefundAmount,
  formRef,
  handleLateChangeOfMindFieldChange,
  onCustomerRefundAmountChange,
  currencyCode,
}: Props) {
  const {
    isToggled: isPercentageToolVisible,
    toggleOn: showPercentageTool,
    toggleOff: hidePercentageTool,
  } = useToggleState();

  const [refundToCustomer, setRefundToCustomer] = useState(maxRefundAmount);
  const [refundPercentage, setRefundPercentage] = useState(100);

  // Debounce expensive downstream updates to avoid cascading recalculations on every keystroke
  const debouncedFieldChange = useMemo(
    () => debounce((formData: FormData) => handleLateChangeOfMindFieldChange(formData), 500),
    [handleLateChangeOfMindFieldChange],
  );

  useEffect(() => {
    return () => {
      debouncedFieldChange.cancel();
    };
  }, [debouncedFieldChange]);

  const handleRefundToCustomerChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const formData = new FormData(formRef.current);
      const raw = (event.target.value as string) ?? '';
      const numeric = Number(raw);
      const safeAmount = Number.isFinite(numeric) ? numeric : 0;
      let recalculatedPercentage = parseFloat(((safeAmount / maxRefundAmount) * 100).toFixed(2));
      let finalAmountNum = safeAmount;

      if (recalculatedPercentage > 100 || safeAmount > maxRefundAmount) {
        recalculatedPercentage = 100;
        finalAmountNum = maxRefundAmount;
        setRefundToCustomer(maxRefundAmount);
        formData.set('percentageRefundToCustomer', maxRefundAmount.toString());
      } else {
        setRefundToCustomer(safeAmount);
        formData.set('percentageRefundToCustomer', safeAmount.toString());
      }

      onCustomerRefundAmountChange(finalAmountNum.toString());
      setRefundPercentage(recalculatedPercentage);
      formData.set('percentageRefund', recalculatedPercentage.toString());
      debouncedFieldChange(formData);
    },
    [formRef, debouncedFieldChange, maxRefundAmount, onCustomerRefundAmountChange],
  );

  const handleRefundPercentageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let newPercentageNum = Number(event.target.value);
      if (!Number.isFinite(newPercentageNum)) {
        newPercentageNum = 0;
      }
      if (newPercentageNum > 100) {
        newPercentageNum = 100;
      } else if (newPercentageNum < 0) {
        newPercentageNum = 0;
      }
      setRefundPercentage(newPercentageNum);
      const recalculatedAmountToCustomer = ((maxRefundAmount / 100) * newPercentageNum).toFixed(2);
      setRefundToCustomer(Number(recalculatedAmountToCustomer));

      const formData = new FormData(formRef.current);
      formData.set('percentageRefundToCustomer', recalculatedAmountToCustomer);
      formData.set('percentageRefund', newPercentageNum.toString());
      onCustomerRefundAmountChange(recalculatedAmountToCustomer);
      debouncedFieldChange(formData);
    },
    [formRef, debouncedFieldChange, maxRefundAmount, onCustomerRefundAmountChange],
  );

  return (
    <Box marginTop={5}>
      <Stack direction="row" spacing={4} marginBottom={3} pt={1}>
        <TextField
          data-cy="RefundToCustomerAmount"
          InputLabelProps={{ shrink: !!refundToCustomer || undefined }}
          label={`Refund to Customer (${currencyCode})`}
          name="percentageRefundToCustomer"
          onChange={handleRefundToCustomerChange}
          placeholder="Enter amount"
          required
          type="Number"
          value={refundToCustomer}
          slotProps={{
            htmlInput: {
              min: 0,
              step: 0.01,
            },
          }}
        />
        {!isPercentageToolVisible && (
          <Tooltip title="Calculate refund amount based on percentage of total booking value">
            <Button onClick={showPercentageTool} data-cy="percentage_tool" color="primary" variant="outlined">
              Use Percentage Calculator
            </Button>
          </Tooltip>
        )}
        {/* Hidden input to hold the refundPercentage tab value when its not visible */}
        <input type="hidden" name="percentageRefund" value={refundPercentage} />
        {isPercentageToolVisible && (
          <Stack direction="row" alignItems="center">
            <Box maxWidth="120px">
              <TextField
                label="% of sell price"
                type="number"
                name="percentageRefund"
                data-cy="% of total"
                value={refundPercentage}
                onChange={handleRefundPercentageChange}
                required
                defaultValue={0}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
              />
            </Box>
            <IconButton onClick={hidePercentageTool} color="error">
              <CancelIcon />
            </IconButton>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}
